import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';
import { useAuth } from '@/modules/auth/presentation/context/AuthContext';
import { useProfile } from '@/modules/profiles/presentation/context/ProfileContext';
import { resolveStructureCode } from '@/modules/profiles/domain/entities/AccessRole';
import { CardReader } from '@/presentation/components/CardReader';
import { AuthMethodSwitcher } from '@/presentation/components/AuthMethodSwitcher';
import { useRegisterTimelog } from '../hooks/useRegisterTimelog';
import type { AttendanceAction } from '../../domain/entities/Timelog';
import { unlockAdminSession } from '@/modules/admin/application/adminSession';
import { useCardReader } from '../hooks/useCardReader';
import { getEmployeeLookupRepository, getLocalEmployeeRepository } from '../../infrastructure/repositories/container';
// OLD: getTimelogRepository no longer needed (automatic mode now uses accessType = 0)
import { OnlineTimelogError } from '../../infrastructure/repositories/CompositeTimelogRepository';
import { isDefinitelyOnline } from '@/shared/onlineState';
import { MOCK_CARD_EMPLOYEE_DTO } from '../../infrastructure/repositories/__tests__/fixtures/mockCardEmployee';
import { useGeolocation } from '@/shared/hooks/useGeolocation';
import { useResolvedAddress } from '@/shared/hooks/useResolvedAddress';
import { CLOCK_WEB_MODE, TIMELOG_SOURCE } from '../../domain/constants/verificationMode';
import { useQualitySettings } from '@/shared/hooks/useQualitySettings';

// ── helpers ───────────────────────────────────────────────────────────────────

function actionColor(
  action: AttendanceAction,
  C: { success: string; danger: string; blue: string; primary: string },
): string {
  if (action === 'entrada') return C.blue; // was C.success — unified to C.blue (Slice B)
  if (action === 'salida') return C.blue; // was C.danger  — unified to C.blue (Slice B)
  if (action === 'automatic') return C.blue; // system decides — same unified blue
  if (action === 'acceso-admin') return C.blue;
  return C.blue; // acceso-empleado: was C.primary (same value) — explicit now
}

// ── Phase type ────────────────────────────────────────────────────────────────

type Phase = 'idle' | 'validating';

const IDLE_TO_VALIDATING_MS = 600;

// ── Component ─────────────────────────────────────────────────────────────────

export function CardScanPage() {
  const { t } = useTranslation('common');
  const { C, T } = useTheme();
  const navigate = useNavigate();
  const { action = 'entrada' } = useParams<{ action: string }>();
  const auth = useAuth();
  const { selectedRole } = useProfile();
  const typedAction = action as AttendanceAction;
  const accent = actionColor(typedAction, C);
  const isAdmin = typedAction === 'acceso-admin';

  // ── lookup repo via container — Composite repo enriches with photoUrl ────────
  const lookupRepo = getEmployeeLookupRepository();
  // ── local repo for DEV seed only (upsertAll not available on Composite) ──────
  const localRepo = getLocalEmployeeRepository();

  const { coordinates } = useGeolocation();
  const { address } = useResolvedAddress(coordinates);
  const { consecutiveTimelogMinSeconds } = useQualitySettings();
  const [phase, setPhase] = useState<Phase>('idle');
  const registerTimelog = useRegisterTimelog();

  // ── Auto-transition idle → validating after 600ms (matches AdminEnrollCardPage) ───
  useEffect(() => {
    const timer = setTimeout(() => {
      setPhase('validating');
    }, IDLE_TO_VALIDATING_MS);
    return () => { clearTimeout(timer); };
  }, []);

  // ── DEV: seed mock card employee on mount ───────────────────────────────────
  useEffect(() => {
    if (import.meta.env.DEV && selectedRole) {
      void localRepo.upsertAll(selectedRole.idAccessRole, [MOCK_CARD_EMPLOYEE_DTO]);
    }
  }, [selectedRole, localRepo]);

  // ── card submit handler ─────────────────────────────────────────────────────
  const handleCardSubmit = useCallback(
    async (cardValue: string) => {
      if (!auth.session || !selectedRole) {
        void navigate('/error', { state: { action: typedAction, method: 'nfc' }, replace: true });
        return;
      }

      setPhase('validating');

      const employee = await lookupRepo.findByCardNumber(selectedRole.idAccessRole, cardValue);

      if (!employee) {
        setPhase('idle');
        void navigate('/error', { state: { action: typedAction, method: 'nfc' }, replace: true });
        return;
      }

      try {
        if (typedAction === 'acceso-empleado') {
          void navigate(`/empleado/${employee.idPerson}`, {
            state: { employee },
            replace: true,
          });
          return;
        }

        if (typedAction !== 'acceso-admin') {
          // entrada / salida / automatic — register timelog via use case.
          // For 'automatic', the accessType is derived from the person's last
          // NEW: Use accessType = 0 for automatic mode (backend decides entrada/salida)
          // OLD: Client-side logic looked up last timelog and toggled 1→2 or 2→1
          let accessType: 0 | 1 | 2 = typedAction === 'salida' ? 2 : typedAction === 'entrada' ? 1 : 0;
          let resolvedAction: AttendanceAction = 
            accessType === 2 ? 'salida' : 
            accessType === 1 ? 'entrada' : 
            'automatic';
          
          // OLD automatic logic (commented for rollback):
          // let accessType: 1 | 2 = typedAction === 'salida' ? 2 : 1;
          // if (typedAction === 'automatic') {
          //   try {
          //     const repo = getTimelogRepository();
          //     const last = await repo.getLastByPerson(employee.idPerson);
          //     accessType = last?.accessType === 1 ? 2 : 1;
          //   } catch (err) {
          //     console.warn('[CardScanPage] Automatic lookup failed — defaulting to entrada:', err);
          //     accessType = 1;
          //   }
          // }
          // const resolvedAction: AttendanceAction = accessType === 2 ? 'salida' : 'entrada';

          const cardIdStructure = selectedRole.structures[0] ?? 0;

          await registerTimelog.execute({
            employee: { idEntity: employee.idEntity, idBiometric: null },
            idStructure: cardIdStructure,
            structureCode: resolveStructureCode(selectedRole, cardIdStructure),
            accessType,
            verificationMode: CLOCK_WEB_MODE.card,
            source: TIMELOG_SOURCE.clockWeb,
            coordinates: coordinates ?? { lat: 0, lng: 0 },
            location: address,
            isOffline: !isDefinitelyOnline(),
            consecutiveMinSeconds: consecutiveTimelogMinSeconds,
            // idDevice omitted → RegisterTimelogUseCase resolves via LocalStorageDeviceIdProvider
            // timezone omitted → use case: Intl.DateTimeFormat().resolvedOptions().timeZone
            // datetime omitted → use case: new Date().toISOString()
          });
          void navigate('/success', { state: { action: resolvedAction, employee }, replace: true });
          return;
        }

        // acceso-admin — no timelog, just unlock session
        unlockAdminSession();
        void navigate('/admin', { replace: true });
      } catch (err) {
        setPhase('idle');
        // OnlineTimelogError = backend rejection (online) or consecutive
        // guard violation (offline). `code` + `elapsedSeconds` tell
        // MarkingErrorPage which copy to render.
        const errorCode = err instanceof OnlineTimelogError ? err.code : undefined;
        const elapsedSeconds = err instanceof OnlineTimelogError ? err.elapsedSeconds : undefined;
        const minSeconds = err instanceof OnlineTimelogError ? err.minSeconds : undefined;
        void navigate('/error', {
          state: { action: typedAction, method: 'nfc', errorCode, elapsedSeconds, minSeconds },
          replace: true,
        });
      }
    },
    [auth.session, selectedRole, typedAction, navigate, registerTimelog, lookupRepo, coordinates, address],
  );

  const { inputRef, handleBlur, handleChange, handleKeyDown } = useCardReader(
    (v) => { void handleCardSubmit(v); },
  );

  const isValidating = phase === 'validating';
  const isEmployee = typedAction === 'acceso-empleado';
  const isAttendance = !isAdmin && !isEmployee;

  return (
    <div
      style={{
        flex: 1,
        background: T.bg,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '30px 30px 26px',
        overflow: 'hidden',
      }}
    >
      {/* Hidden input — focus trap for HID card reader.
          Uses aria-label (not aria-hidden) to avoid blocking focus for
          assistive technology. Visually invisible via CSS only. */}
      <input
        ref={inputRef}
        type="text"
        aria-label="Card reader input"
        tabIndex={-1}
        autoComplete="off"
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        style={{
          position: 'fixed',
          opacity: 0,
          width: 1,
          height: 1,
          top: 0,
          left: 0,
          pointerEvents: 'none',
        }}
      />

      <div
        className="mi-anim-fade-in"
        style={{
          flex: 1,
          width: '100%',
          maxWidth: 540,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0,
        }}
      >
        {/* Header — mirrors ScanningPage.tsx structure for visual parity:
            back button on left, absolute-centered persistent title, role badge
            right-aligned via marginLeft: 'auto'. Spec: attendance-scan-screen-parity. */}
        <div
          style={{
            width: '100%',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            marginBottom: 24,
          }}
        >
          <button
            onClick={() => { void navigate('/'); }}
            style={{
              background: 'transparent',
              border: `1px solid ${T.border}`,
              borderRadius: 8,
              padding: '7px 12px',
              cursor: 'pointer',
              color: T.text,
              fontFamily: 'inherit',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 11,
              fontWeight: 600,
              zIndex: 1,
            }}
          >
            <svg
              width={11}
              height={11}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
            {t('scan.back')}
          </button>
          {/* Persistent title — absolutely centered across the full header row.
              Uses cardScan.title{Card|Access|Admin} keys so each action keeps
              its own copy without losing the title to the badge. */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              textAlign: 'center',
              fontSize: 14,
              fontWeight: 700,
              color: T.text,
              pointerEvents: 'none',
            }}
          >
            {typedAction === 'acceso-empleado'
              ? t('cardScan.titleAccess')
              : typedAction === 'acceso-admin'
                ? t('cardScan.titleAdmin')
                : t('cardScan.titleCard')}
          </div>
          <div
            style={{
              marginLeft: 'auto',
              padding: '5px 15px',
              borderRadius: 15,
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '.06em',
              zIndex: 1,
              background: `${accent}18`,
              color: accent,
            }}
          >
            {typedAction === 'entrada'
              ? t('scan.labelEntry')
              : typedAction === 'salida'
                ? t('scan.labelExit')
                : typedAction === 'acceso-admin'
                  ? t('scan.labelAdmin')
                  : t('scan.labelAccess')}
          </div>
        </div>

        {/* Card reader icon — wrapper margins match ScanningPage's camera
            wrapper (marginTop: 48 / marginBottom: 65) so the scan circle sits
            at the same vertical Y across both pages. */}
        <div style={{ marginTop: 48, marginBottom: 65 }}>
          <CardReader
            scanning={isValidating}
            size={195}
            animationClass="mi-card-pulse-ring"
            T={T}
          />
        </div>

        {/* Status text */}
        <div
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: T.clockText,
            marginBottom: 8,
            textAlign: 'center',
          }}
        >
          {isValidating ? t('cardScan.validating') : t('cardScan.instruction')}
        </div>
        <div
          style={{
            fontSize: 12,
            color: T.textMuted,
            textAlign: 'center',
            maxWidth: 360,
            lineHeight: 1.6,
            marginBottom: 24,
          }}
        >
          {isValidating ? t('cardScan.readingHint') : t('cardScan.promptHint')}
        </div>

        {/* Spinner while validating */}
        {isValidating && (
          <div
            className="mi-anim-spin"
            style={{
              width: 22,
              height: 22,
              border: `2px solid ${T.textMuted}`,
              borderTopColor: C.primary,
              borderRadius: '50%',
            }}
          />
        )}

        {/* Alt methods — attendance flow (entrada/salida) — shared AuthMethodSwitcher */}
        {isAttendance && (
          <div style={{ marginTop: 18 }}>
            <AuthMethodSwitcher
              currentMethod="nfc"
              action={typedAction}
              accent={accent}
              t={t}
            />
          </div>
        )}

        {/* Alt methods — shown for admin and employee flows */}
        {(isAdmin || isEmployee) && (
          <div
            style={{
              display: 'flex',
              gap: 10,
              marginTop: 18,
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            <button
              onClick={() => { void navigate(`/scan/${typedAction}`); }}
              style={{
                padding: '8px 18px',
                borderRadius: 8,
                border: `1px solid ${accent}40`,
                background: `${accent}10`,
                color: accent,
                fontFamily: 'inherit',
                fontSize: 10,
                fontWeight: 700,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <svg
                width={11}
                height={11}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="9" r="3" />
                <path d="M5 20a7 7 0 0 1 14 0" />
                <path d="M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0z" />
              </svg>
              {t('methods.useFace')}
            </button>
            <button
              onClick={() => { void navigate(`/id-input/${typedAction}`); }}
              style={{
                padding: '8px 18px',
                borderRadius: 8,
                border: `1px solid ${accent}40`,
                background: `${accent}10`,
                color: accent,
                fontFamily: 'inherit',
                fontSize: 10,
                fontWeight: 700,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <svg
                width={11}
                height={11}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              {t('methods.usePin')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
